'use client';
import { createClient } from '@/lib/supabase/client';

// ---- Types ----
export type CarCategory = 'Economy' | 'Comfort' | 'SUV';
export type CarStatus = 'Available' | 'Reserved' | 'Hidden';
export type FuelType = 'Petrol' | 'Diesel' | 'Hybrid' | 'Hybrid AWD' | 'Diesel AWD' | 'Electric';
export type GearboxType = 'Manual' | 'Automatic';
export type ReservationStatus = 'Pending' | 'Confirmed' | 'Completed' | 'Cancelled';

export interface CarImage {
  id: string;
  url: string;
  alt: string;
  type: 'Exterior' | 'Interior';
  isMain: boolean;
  sortOrder: number;
}

export interface Car {
  id: string;
  model: string;
  brand: string;
  year: number;
  category: CarCategory;
  pricePerDay: number;
  seats: number;
  gearbox: GearboxType;
  fuel: FuelType;
  drive?: string;
  doors?: number;
  luggageCapacity?: number;
  hasAc?: boolean;
  status: CarStatus;
  description: string;
  images: CarImage[];
}

export interface AddOnSelection {
  id: string;
  name: string;
  pricePerDay: number;
  days: number;
  subtotal: number;
}

export interface Reservation {
  id: string;
  bookingRef: string;
  fullName: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  carId: string;
  carModel: string;
  carBrand: string;
  carCategory: string;
  pricePerDay: number;
  visitDateTime: string;
  pickupDate: string;
  returnDate: string;
  days: number;
  addOns: AddOnSelection[];
  carSubtotal: number;
  addOnsSubtotal: number;
  discountPercent: number;
  discountAmount: number;
  totalPrice: number;
  status: ReservationStatus;
  createdAt: string;
  notes?: string;
  marketingConsent: boolean;
  marketingConsentAt?: string;
}

export interface Brand {
  id: string;
  name: string;
  logoUrl?: string;
}

export interface Setting {
  key: string;
  value: string;
}

// ---- Mappers ----
function mapCar(row: any, images: any[]): Car {
  return {
    id: row.id,
    model: row.model,
    brand: row.brand,
    year: row.year || 2023,
    category: row.category as CarCategory,
    pricePerDay: row.price_per_day,
    seats: row.seats,
    gearbox: row.gearbox as GearboxType,
    fuel: row.fuel as FuelType,
    drive: row.drive || undefined,
    doors: row.doors || undefined,
    luggageCapacity: row.luggage_capacity || undefined,
    hasAc: row.has_ac ?? true,
    status: row.car_status as CarStatus,
    description: row.description || '',
    images: images
      .filter((i) => i.car_id === row.id)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((i) => ({
        id: i.id,
        url: i.url,
        alt: i.alt || '',
        type: i.image_type as 'Exterior' | 'Interior',
        isMain: i.is_main,
        sortOrder: i.sort_order,
      })),
  };
}

function mapReservation(row: any): Reservation {
  return {
    id: row.id,
    bookingRef: row.booking_ref,
    fullName: row.full_name,
    email: row.email,
    phone: row.phone,
    address: row.address || '',
    postalCode: row.postal_code || '',
    city: row.city || '',
    carId: row.car_id || '',
    carModel: row.car_model,
    carBrand: row.car_brand,
    carCategory: row.car_category,
    pricePerDay: row.price_per_day,
    visitDateTime: row.visit_date_time || '',
    pickupDate: row.pickup_date,
    returnDate: row.return_date,
    days: row.days,
    addOns: (row.reservation_addons || []).map((a: any) => ({
      id: a.addon_id,
      name: a.name,
      pricePerDay: a.price_per_day,
      days: a.days,
      subtotal: a.subtotal,
    })),
    carSubtotal: row.car_subtotal,
    addOnsSubtotal: row.addons_subtotal,
    discountPercent: row.discount_percent,
    discountAmount: row.discount_amount,
    totalPrice: row.total_price,
    status: row.res_status as ReservationStatus,
    createdAt: row.created_at,
    notes: row.notes || '',
    marketingConsent: row.marketing_consent ?? false,
    marketingConsentAt: row.marketing_consent_at || undefined,
  };
}

// ---- Cars ----
export async function fetchCars(): Promise<Car[]> {
  const supabase = createClient();
  const { data: cars, error: carsError } = await supabase
    .from('cars')
    .select('*')
    .order('model', { ascending: true });
  if (carsError) { console.error('fetchCars error:', carsError.message); return []; }

  const { data: images, error: imagesError } = await supabase
    .from('car_images')
    .select('*')
    .order('sort_order', { ascending: true });
  if (imagesError) { console.error('fetchCarImages error:', imagesError.message); }

  return (cars || []).map((c) => mapCar(c, images || []));
}

export async function fetchCarById(id: string): Promise<Car | null> {
  const supabase = createClient();
  const { data: car, error } = await supabase.from('cars').select('*').eq('id', id).single();
  if (error || !car) return null;
  const { data: images } = await supabase.from('car_images').select('*').eq('car_id', id).order('sort_order');
  return mapCar(car, (images || []).map((i) => ({ ...i, car_id: id })));
}

export async function createCar(car: Omit<Car, 'id' | 'images'>): Promise<Car | null> {
  const supabase = createClient();
  const { data, error } = await supabase.from('cars').insert({
    model: car.model, brand: car.brand, year: car.year, category: car.category,
    price_per_day: car.pricePerDay, seats: car.seats, gearbox: car.gearbox,
    fuel: car.fuel, drive: car.drive || null, doors: car.doors || null,
    luggage_capacity: car.luggageCapacity || null, has_ac: car.hasAc ?? true,
    car_status: car.status, description: car.description,
  }).select().single();
  if (error) { console.error('createCar error:', error.message); return null; }
  return mapCar(data, []);
}

export async function updateCar(id: string, car: Partial<Omit<Car, 'id' | 'images'>>): Promise<boolean> {
  const supabase = createClient();
  const update: any = {};
  if (car.model !== undefined) update.model = car.model;
  if (car.brand !== undefined) update.brand = car.brand;
  if (car.year !== undefined) update.year = car.year;
  if (car.category !== undefined) update.category = car.category;
  if (car.pricePerDay !== undefined) update.price_per_day = car.pricePerDay;
  if (car.seats !== undefined) update.seats = car.seats;
  if (car.gearbox !== undefined) update.gearbox = car.gearbox;
  if (car.fuel !== undefined) update.fuel = car.fuel;
  if (car.drive !== undefined) update.drive = car.drive || null;
  if (car.doors !== undefined) update.doors = car.doors || null;
  if (car.luggageCapacity !== undefined) update.luggage_capacity = car.luggageCapacity || null;
  if (car.hasAc !== undefined) update.has_ac = car.hasAc;
  if (car.status !== undefined) update.car_status = car.status;
  if (car.description !== undefined) update.description = car.description;
  const { error } = await supabase.from('cars').update(update).eq('id', id);
  if (error) { console.error('updateCar error:', error.message); return false; }
  return true;
}

export async function deleteCar(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('cars').delete().eq('id', id);
  if (error) { console.error('deleteCar error:', error.message); return false; }
  return true;
}

// ---- Car Images ----
export async function updateCarImages(carId: string, images: CarImage[]): Promise<boolean> {
  const supabase = createClient();
  // Delete all existing images for this car
  const { error: delError } = await supabase.from('car_images').delete().eq('car_id', carId);
  if (delError) { console.error('deleteCarImages error:', delError.message); return false; }
  if (images.length === 0) return true;
  // Insert new images
  const rows = images.map((img, idx) => ({
    car_id: carId, url: img.url, alt: img.alt,
    image_type: img.type, is_main: img.isMain, sort_order: idx,
  }));
  const { error: insError } = await supabase.from('car_images').insert(rows);
  if (insError) { console.error('insertCarImages error:', insError.message); return false; }
  return true;
}

// ---- Image Upload ----
export async function uploadCarImage(
  file: File,
  carId: string,
  onProgress?: (pct: number) => void
): Promise<string | null> {
  const supabase = createClient();
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `${carId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
  onProgress?.(10);
  const { data, error } = await supabase.storage
    .from('car-images')
    .upload(path, file, { upsert: false, contentType: file.type });
  if (error) { console.error('uploadCarImage error:', error.message); return null; }
  onProgress?.(100);
  const { data: urlData } = supabase.storage.from('car-images').getPublicUrl(data.path);
  return urlData.publicUrl;
}

export async function deleteCarImageFromStorage(url: string): Promise<void> {
  const supabase = createClient();
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/car-images/');
    if (pathParts.length < 2) return;
    await supabase.storage.from('car-images').remove([pathParts[1]]);
  } catch {
    // ignore
  }
}

// ---- Reservations ----
export async function fetchReservations(): Promise<Reservation[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('reservations')
    .select('*, reservation_addons(*)')
    .order('created_at', { ascending: false });
  if (error) { console.error('fetchReservations error:', error.message); return []; }
  return (data || []).map(mapReservation);
}

export async function createReservation(res: Omit<Reservation, 'id' | 'createdAt'>): Promise<string | null> {
  const supabase = createClient();
  const { data, error } = await supabase.from('reservations').insert({
    booking_ref: res.bookingRef,
    full_name: res.fullName, email: res.email, phone: res.phone,
    address: res.address, postal_code: res.postalCode, city: res.city,
    car_id: res.carId || null, car_model: res.carModel, car_brand: res.carBrand,
    car_category: res.carCategory, price_per_day: res.pricePerDay,
    visit_date_time: res.visitDateTime, pickup_date: res.pickupDate,
    return_date: res.returnDate, days: res.days,
    car_subtotal: res.carSubtotal, addons_subtotal: res.addOnsSubtotal,
    discount_percent: res.discountPercent, discount_amount: res.discountAmount,
    total_price: res.totalPrice, res_status: 'Pending',
    marketing_consent: res.marketingConsent,
    marketing_consent_at: res.marketingConsent ? new Date().toISOString() : null,
  }).select('id').single();
  if (error) { console.error('createReservation error:', error.message); return null; }
  // Insert add-ons
  if (res.addOns.length > 0) {
    const addons = res.addOns.map((a) => ({
      reservation_id: data.id, addon_id: a.id, name: a.name,
      price_per_day: a.pricePerDay, days: a.days, subtotal: a.subtotal,
    }));
    await supabase.from('reservation_addons').insert(addons);
  }
  return data.id;
}

export async function updateReservationStatus(id: string, status: ReservationStatus): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('reservations').update({ res_status: status }).eq('id', id);
  if (error) { console.error('updateReservationStatus error:', error.message); return false; }
  return true;
}

// ---- Brands ----
export async function fetchBrands(): Promise<Brand[]> {
  const supabase = createClient();
  const { data, error } = await supabase.from('brands').select('*').order('name');
  if (error) { console.error('fetchBrands error:', error.message); return []; }
  return (data || []).map((b) => ({ id: b.id, name: b.name, logoUrl: b.logo_url || undefined }));
}

export async function createBrand(name: string, logoUrl?: string): Promise<Brand | null> {
  const supabase = createClient();
  const { data, error } = await supabase.from('brands').insert({ name, logo_url: logoUrl || null }).select().single();
  if (error) { console.error('createBrand error:', error.message); return null; }
  return { id: data.id, name: data.name, logoUrl: data.logo_url || undefined };
}

export async function updateBrand(id: string, name: string, logoUrl?: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('brands').update({ name, logo_url: logoUrl || null }).eq('id', id);
  if (error) { console.error('updateBrand error:', error.message); return false; }
  return true;
}

export async function deleteBrand(id: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('brands').delete().eq('id', id);
  if (error) { console.error('deleteBrand error:', error.message); return false; }
  return true;
}

// ---- Settings ----
export async function fetchSettings(): Promise<Record<string, string>> {
  const supabase = createClient();
  const { data, error } = await supabase.from('settings').select('key, value');
  if (error) { console.error('fetchSettings error:', error.message); return {}; }
  return Object.fromEntries((data || []).map((s) => [s.key, s.value]));
}

export async function upsertSetting(key: string, value: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.from('settings').upsert({ key, value }, { onConflict: 'key' });
  if (error) { console.error('upsertSetting error:', error.message); return false; }
  return true;
}

export async function upsertSettings(settings: Record<string, string>): Promise<boolean> {
  const supabase = createClient();
  const rows = Object.entries(settings).map(([key, value]) => ({ key, value }));
  const { error } = await supabase.from('settings').upsert(rows, { onConflict: 'key' });
  if (error) { console.error('upsertSettings error:', error.message); return false; }
  return true;
}

export async function changeAdminPassword(newPassword: string): Promise<boolean> {
  const supabase = createClient();
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) { console.error('changeAdminPassword error:', error.message); return false; }
  return true;
}
